<template>
  <div class="marketing-shop">
    <header class="shop-header">
      <div class="content-shell hero-content">
        <div class="hero-copy">
          <div class="eyebrow">Marketing &amp; Kommunikation</div>
          <h1>Marketing Shop</h1>
          <p>Werbemittel auswählen, zentral bestellen und jederzeit den Überblick behalten.</p>
          <div class="context-line">
            <v-chip small outlined color="success" class="context-chip">
              <v-icon left small>mdi-map-marker-outline</v-icon>
              {{ selectedStudioName }}
            </v-chip>
            <span>Interner Shop · Bestellung auf Rechnung des Studios</span>
          </div>
        </div>

        <div class="hero-actions">
          <v-btn-toggle
            v-if="canManage"
            v-model="workspaceMode"
            mandatory
            dense
            dark
            color="primary"
            class="workspace-switch"
            aria-label="Bereich auswählen">
            <v-btn value="store" class="text-none">
              <v-icon left small>mdi-storefront-outline</v-icon>
              Shop
            </v-btn>
            <v-btn value="manage" class="text-none">
              <v-icon left small>mdi-view-dashboard-outline</v-icon>
              Verwaltung
            </v-btn>
          </v-btn-toggle>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                large
                class="cart-btn"
                :class="{ 'cart-bump': cartBump }"
                aria-label="Warenkorb öffnen"
                @click="openCart">
                <v-badge :content="cartItemCount" :value="cartItemCount > 0" color="error" overlap>
                  <v-icon size="28" color="white">mdi-cart-outline</v-icon>
                </v-badge>
              </v-btn>
            </template>
            <span>Warenkorb öffnen</span>
          </v-tooltip>
        </div>
      </div>

      <div class="content-shell tab-shell">
        <div class="tab-context">{{ workspaceMode === "manage" ? "Verwaltung" : "Einkauf" }}</div>
        <v-tabs v-model="activeTab" background-color="transparent" dark color="primary" slider-size="3" class="shop-tabs" show-arrows>
          <template v-if="workspaceMode === 'store'">
            <v-tab href="#shop" class="text-none">
              <v-icon left small>mdi-storefront</v-icon>
              Produkte
            </v-tab>
            <v-tab href="#myOrders" class="text-none">
              <v-icon left small>mdi-package-variant-closed</v-icon>
              Meine Bestellungen
            </v-tab>
            <v-tab href="#inventory" class="text-none">
              <v-icon left small>mdi-clipboard-text-multiple-outline</v-icon>
              Bestellte Werbemittel
            </v-tab>
          </template>
          <template v-else-if="canManage">
            <v-tab href="#allOrders" class="text-none">
              <v-icon left small>mdi-clipboard-list-outline</v-icon>
              Bestellungen
            </v-tab>
            <v-tab href="#productAdmin" class="text-none">
              <v-icon left small>mdi-package-variant-plus</v-icon>
              Produkte
            </v-tab>
          </template>
        </v-tabs>
      </div>
    </header>

    <main class="shop-content">
      <v-tabs-items v-model="activeTab" class="transparent">
        <v-tab-item value="shop">
          <section class="content-shell content-section" aria-labelledby="product-overview-title">
            <div class="section-heading">
              <div>
                <h2 id="product-overview-title">Produkte für dein Studio</h2>
                <p>Finde physische Werbemittel und digitale Vorlagen an einem Ort.</p>
              </div>
              <div class="result-count" aria-live="polite">{{ filteredProducts.length }} von {{ products.length }} Produkten</div>
            </div>

            <v-card dark flat class="filter-panel">
              <v-row dense align="center">
                <v-col cols="12" :md="canManage ? 5 : 8">
                  <v-text-field
                    v-model="productSearch"
                    prepend-inner-icon="mdi-magnify"
                    label="Produkte durchsuchen"
                    placeholder="z. B. Flyer, Google Karte oder Vorlage"
                    outlined
                    dense
                    dark
                    hide-details
                    clearable />
                </v-col>
                <v-col v-if="canManage" cols="12" sm="6" md="3">
                  <v-select
                    v-model="shopStudioFilter"
                    :items="studioOptions"
                    item-text="name"
                    item-value="_id"
                    prepend-inner-icon="mdi-store-marker-outline"
                    label="Studio-Vorschau"
                    outlined
                    dense
                    dark
                    hide-details />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="productSort"
                    :items="productSortOptions"
                    item-text="text"
                    item-value="value"
                    prepend-inner-icon="mdi-sort-variant"
                    label="Sortierung"
                    outlined
                    dense
                    dark
                    hide-details />
                </v-col>
              </v-row>

              <div class="filter-row">
                <v-btn-toggle v-model="productTypeFilter" mandatory dense color="primary" dark class="type-toggle">
                  <v-btn value="all" class="text-none">
                    <v-icon left small>mdi-view-grid-outline</v-icon>
                    Alle
                  </v-btn>
                  <v-btn value="physical" class="text-none">
                    <v-icon left small>mdi-package-variant</v-icon>
                    Physisch
                  </v-btn>
                  <v-btn value="digital" class="text-none">
                    <v-icon left small>mdi-download-outline</v-icon>
                    Digital
                  </v-btn>
                </v-btn-toggle>

                <v-btn v-if="hasProductFilters" text small color="grey lighten-1" class="text-none reset-filter" @click="resetProductFilters">
                  <v-icon left small>mdi-filter-off-outline</v-icon>
                  Filter zurücksetzen
                </v-btn>
              </div>
            </v-card>

            <v-alert v-if="loadErrors.products" type="error" text dark class="state-alert">
              <div class="state-alert__content">
                <span>{{ loadErrors.products }}</span>
                <v-btn small text color="error" class="text-none" @click="loadProducts">Erneut versuchen</v-btn>
              </div>
            </v-alert>

            <div v-if="loadingProducts" class="product-grid" aria-label="Produkte werden geladen">
              <v-skeleton-loader v-for="n in 8" :key="n" dark type="image, article, actions" class="product-skeleton" />
            </div>

            <div v-else-if="!loadErrors.products && filteredProducts.length === 0" class="empty-state">
              <div class="empty-state__icon"><v-icon size="42">mdi-package-variant-search</v-icon></div>
              <h3>Keine passenden Produkte</h3>
              <p>Versuche einen anderen Suchbegriff oder entferne einzelne Filter.</p>
              <v-btn color="primary" depressed rounded class="text-none" @click="resetProductFilters">Alle Produkte anzeigen</v-btn>
            </div>

            <transition-group v-else-if="!loadErrors.products" name="product-list" tag="div" class="product-grid">
              <product-card
                v-for="product in filteredProducts"
                :key="product._id"
                :product="product"
                :cart-quantity="getCartQuantity(product._id)"
                @add-to-cart="addToCart"
                @open-detail="openProductDetail" />
            </transition-group>
          </section>
        </v-tab-item>

        <v-tab-item value="myOrders">
          <section class="content-shell content-section">
            <v-alert v-if="loadErrors.myOrders" type="error" text dark class="state-alert">
              <div class="state-alert__content">
                <span>{{ loadErrors.myOrders }}</span>
                <v-btn small text color="error" class="text-none" @click="loadMyOrders">Erneut versuchen</v-btn>
              </div>
            </v-alert>
            <my-orders-list v-else :orders="myOrders" :loading="loadingMyOrders" @open-detail="openOrderDetail" />
          </section>
        </v-tab-item>

        <v-tab-item value="inventory">
          <section class="content-shell content-section">
            <div class="section-heading">
              <div>
                <h2>Bestellte Werbemittel</h2>
                <p>Kumulierte Mengen aller bisher bestellten physischen Werbemittel – kein aktueller Lagerbestand.</p>
              </div>
              <div v-if="filteredInventory.length" class="result-count">
                {{ filteredInventory.length }} Positionen · {{ inventoryTotalQuantity }} Stück
              </div>
            </div>

            <v-card dark flat class="filter-panel inventory-filter">
              <div v-if="$vuetify.breakpoint.xsOnly" class="inventory-mobile-filter-controls">
                <v-select
                  v-if="canManage"
                  v-model="inventoryStudio"
                  :items="studioOptions"
                  item-text="name"
                  item-value="_id"
                  label="Studio auswählen"
                  prepend-inner-icon="mdi-map-marker-outline"
                  outlined
                  dense
                  dark
                  hide-details />
                <div v-else class="studio-readonly inventory-mobile-studio">
                  <span>Studio</span>
                  <strong>{{ selectedStudioName }}</strong>
                </div>

                <div class="inventory-mobile-filter-row">
                  <v-text-field
                    v-model="inventorySearch"
                    prepend-inner-icon="mdi-magnify"
                    label="Werbemittel durchsuchen"
                    outlined
                    dense
                    dark
                    hide-details
                    clearable
                    class="inventory-mobile-search" />
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        outlined
                        color="grey lighten-1"
                        class="inventory-mobile-refresh"
                        :loading="loadingInventory"
                        aria-label="Bestellte Werbemittel neu laden"
                        @click="loadInventory">
                        <v-icon>mdi-refresh</v-icon>
                      </v-btn>
                    </template>
                    <span>Neu laden</span>
                  </v-tooltip>
                </div>
              </div>

              <v-row v-else dense align="center">
                <v-col v-if="canManage" cols="12" sm="6" md="4">
                  <v-select
                    v-model="inventoryStudio"
                    :items="studioOptions"
                    item-text="name"
                    item-value="_id"
                    label="Studio auswählen"
                    prepend-inner-icon="mdi-map-marker-outline"
                    outlined
                    dense
                    dark
                    hide-details />
                </v-col>
                <v-col v-else cols="12" sm="6" md="4">
                  <div class="studio-readonly">
                    <span>Studio</span>
                    <strong>{{ selectedStudioName }}</strong>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="5">
                  <v-text-field
                    v-model="inventorySearch"
                    prepend-inner-icon="mdi-magnify"
                    label="Werbemittel durchsuchen"
                    outlined
                    dense
                    dark
                    hide-details
                    clearable />
                </v-col>
                <v-spacer />
                <v-col cols="auto">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        :loading="loadingInventory"
                        aria-label="Bestellte Werbemittel neu laden"
                        @click="loadInventory">
                        <v-icon>mdi-refresh</v-icon>
                      </v-btn>
                    </template>
                    <span>Neu laden</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-card>

            <v-alert v-if="loadErrors.inventory" type="error" text dark class="state-alert">
              <div class="state-alert__content">
                <span>{{ loadErrors.inventory }}</span>
                <v-btn small text color="error" class="text-none" @click="loadInventory">Erneut versuchen</v-btn>
              </div>
            </v-alert>

            <div v-if="loadingInventory" class="inventory-skeletons">
              <v-skeleton-loader v-for="n in 4" :key="n" dark type="list-item-avatar-three-line" />
            </div>

            <div v-else-if="!loadErrors.inventory && filteredInventory.length === 0" class="empty-state">
              <div class="empty-state__icon"><v-icon size="42">mdi-clipboard-text-play-outline</v-icon></div>
              <h3>Noch keine Werbemittel bestellt</h3>
              <p>Für dieses Studio wurden bisher keine physischen Werbemittel erfasst.</p>
              <v-btn color="primary" depressed rounded class="text-none" @click="activeTab = 'shop'">Zum Shop</v-btn>
            </div>

            <template v-else-if="!loadErrors.inventory">
              <div v-if="$vuetify.breakpoint.xsOnly" class="inventory-mobile-list">
                <v-card v-for="item in filteredInventory" :key="getInventoryKey(item)" dark flat class="inventory-mobile-card">
                  <div class="inventory-mobile-card__media" aria-hidden="true">
                    <inventory-product-thumb :product="item.product" :selected-options="item.selectedOptions" />
                  </div>
                  <div class="inventory-mobile-card__body">
                    <strong>{{ item.product.title }}</strong>
                    <span>{{ formatInventoryOptions(item.selectedOptions) }}</span>
                  </div>
                  <div class="inventory-mobile-card__quantity">
                    <span>Gesamt bestellt</span>
                    <v-chip color="success" small class="quantity-chip">{{ item.totalQuantity }} Stk</v-chip>
                  </div>
                </v-card>
              </div>

              <v-card v-else dark flat class="data-surface">
                <v-data-table
                  dark
                  :headers="inventoryHeaders"
                  :items="filteredInventory"
                  :items-per-page="50"
                  class="transparent"
                  no-data-text="Keine Einträge">
                  <template v-slot:item.image="{ item }">
                    <v-avatar size="44" tile rounded class="my-2">
                      <inventory-product-thumb :product="item.product" :selected-options="item.selectedOptions" />
                    </v-avatar>
                  </template>
                  <template v-slot:item.title="{ item }">
                    <span class="font-weight-medium">{{ item.product.title }}</span>
                  </template>
                  <template v-slot:item.variant="{ item }">
                    <template v-if="item.selectedOptions && item.selectedOptions.length > 0">
                      <v-chip v-for="opt in item.selectedOptions" :key="opt.name" x-small outlined color="grey lighten-2" class="mr-1">
                        {{ opt.name }}: {{ opt.value }}
                      </v-chip>
                    </template>
                    <span v-else class="grey--text text-caption">Standardausführung</span>
                  </template>
                  <template v-slot:item.quantity="{ item }">
                    <v-chip color="success" dark small class="font-weight-bold quantity-chip">{{ item.totalQuantity }} Stk</v-chip>
                  </template>
                </v-data-table>
              </v-card>
            </template>
          </section>
        </v-tab-item>

        <v-tab-item v-if="canManage" value="allOrders">
          <section class="content-shell content-section">
            <v-alert v-if="loadErrors.allOrders" type="error" text dark class="state-alert">
              <div class="state-alert__content">
                <span>{{ loadErrors.allOrders }}</span>
                <v-btn small text color="error" class="text-none" @click="loadAllOrders">Erneut versuchen</v-btn>
              </div>
            </v-alert>
            <admin-orders-list
              v-else
              :orders="allOrders"
              :loading="loadingAllOrders"
              :studio-options="studioFilterOptions"
              :filter-studio.sync="filterStudio"
              :filter-status.sync="filterStatus"
              @reload="loadAllOrders"
              @open-detail="openOrderDetail" />
          </section>
        </v-tab-item>

        <v-tab-item v-if="canManage" value="productAdmin">
          <section class="content-shell content-section">
            <v-alert v-if="loadErrors.adminProducts" type="error" text dark class="state-alert">
              <div class="state-alert__content">
                <span>{{ loadErrors.adminProducts }}</span>
                <v-btn small text color="error" class="text-none" @click="loadAdminProducts">Erneut versuchen</v-btn>
              </div>
            </v-alert>
            <product-admin
              v-else
              :studio-options="studioOptions"
              :products="adminProducts"
              :loading="loadingAdminProducts"
              @reload="reloadAllProducts" />
          </section>
        </v-tab-item>
      </v-tabs-items>
    </main>

    <cart-drawer
      v-model="cartOpen"
      :items="cart"
      :products="products"
      :studio-options="studioOptions"
      :default-studio-id="getSelectedStudioId"
      :submitting="submittingOrder"
      @update-quantity="updateCartQuantity"
      @remove-item="removeFromCart"
      @clear-cart="clearCart"
      @submit-order="submitOrder" />

    <product-detail-dialog
      v-model="productDetailOpen"
      :product="currentProduct"
      :cart-quantity="currentProduct ? getCartQuantity(currentProduct._id) : 0"
      @add-to-cart="addToCart" />

    <order-detail-dialog
      v-model="orderDetailOpen"
      :order="currentOrder"
      :can-manage="canManage"
      :available-products="products"
      @reload="reloadCurrentOrder"
      @order-updated="handleOrderUpdated" />

    <v-dialog v-model="orderSuccessOpen" max-width="520" persistent>
      <v-card dark class="order-success-card">
        <div class="success-mark"><v-icon size="42" color="white">mdi-check</v-icon></div>
        <h2>Bestellung ist eingegangen</h2>
        <p>
          Wir haben deine Bestellung <strong>{{ orderSuccess ? orderSuccess.reference : "" }}</strong> erfolgreich erfasst.
        </p>
        <v-alert v-if="orderSuccess && orderSuccess.digitalItems" text color="success" icon="mdi-download-circle-outline" class="success-info">
          Deine Downloads findest du jetzt in den Bestelldetails unter „Meine Bestellungen“.
        </v-alert>
        <v-alert v-if="orderSuccess && orderSuccess.physicalItems" text color="info" icon="mdi-truck-outline" class="success-info">
          Das Marketing-Team prüft die physischen Positionen und aktualisiert dort den Status.
        </v-alert>
        <div class="success-actions">
          <v-btn text class="text-none" @click="continueShopping">Weiter einkaufen</v-btn>
          <v-btn color="primary" depressed rounded class="text-none" @click="showMyOrders">Bestellungen ansehen</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loadingOrderDetail" width="300" persistent>
      <v-card dark class="pa-5 text-center loading-detail-card">
        <v-progress-circular indeterminate color="primary" size="32" />
        <div class="mt-3">Bestellung wird geladen …</div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { mapState, mapGetters } from "vuex"
import socketService from "@/socketService"

import ProductCard from "./components/ProductCard.vue"
import CartDrawer from "./components/CartDrawer.vue"
import ProductDetailDialog from "./components/ProductDetailDialog.vue"
import MyOrdersList from "./components/MyOrdersList.vue"
import AdminOrdersList from "./components/AdminOrdersList.vue"
import OrderDetailDialog from "./components/OrderDetailDialog.vue"
import ProductAdmin from "./components/ProductAdmin.vue"

const InventoryProductThumb = {
  name: "InventoryProductThumb",
  props: {
    product: { type: Object, required: true },
    selectedOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      url: null,
      requestId: 0,
      loading: false,
      failed: false,
      activeFileId: null,
    }
  },
  computed: {
    imageSignature() {
      const options = (this.selectedOptions || [])
        .map((option) => `${option.name}:${option.value}`)
        .sort()
        .join("|")
      return `${this.product && this.product._id ? this.product._id : ""}-${options}-${this.getImageCandidates().join("|")}`
    },
  },
  watch: {
    imageSignature: {
      immediate: true,
      handler() {
        this.loadImage()
      },
    },
  },
  beforeDestroy() {
    this.requestId += 1
    this.revokeCurrentUrl()
  },
  methods: {
    getVariantImageFileId() {
      for (const selectedOption of this.selectedOptions || []) {
        const productOption = (this.product.options || []).find((option) => option.name === selectedOption.name)
        const choice = productOption && (productOption.choices || []).find((entry) => entry.value === selectedOption.value)
        if (choice && choice.imageFileId) return choice.imageFileId
      }
      return null
    },
    getFirstProductImageId() {
      return this.product.images && this.product.images.length ? this.product.images[0].fileId : null
    },
    getImageCandidates() {
      const variantImageId = this.getVariantImageFileId()
      const firstProductImageId = this.getFirstProductImageId()
      return [variantImageId, firstProductImageId].filter((fileId, index, values) => fileId && values.indexOf(fileId) === index)
    },
    revokeCurrentUrl() {
      if (this.url) URL.revokeObjectURL(this.url)
      this.url = null
    },
    async loadImage(fileIds = null) {
      const currentRequest = ++this.requestId
      this.revokeCurrentUrl()
      this.activeFileId = null
      this.failed = false

      const candidates = Array.isArray(fileIds) ? fileIds.filter(Boolean) : this.getImageCandidates()
      if (!candidates.length) {
        this.loading = false
        this.failed = true
        return
      }

      this.loading = true
      for (const fileId of candidates) {
        try {
          const url = await api.marketingShop.loadProductImage(this.product._id, fileId)
          if (currentRequest !== this.requestId) {
            URL.revokeObjectURL(url)
            return
          }
          this.url = url
          this.activeFileId = fileId
          this.loading = false
          return
        } catch (error) {
          if (currentRequest !== this.requestId) return
        }
      }

      if (currentRequest === this.requestId) {
        this.loading = false
        this.failed = true
      }
    },
    handleImageError() {
      const failedFileId = this.activeFileId
      const fallbackFileId = this.getFirstProductImageId()
      this.requestId += 1
      this.revokeCurrentUrl()
      this.activeFileId = null

      if (fallbackFileId && fallbackFileId !== failedFileId) {
        this.loadImage([fallbackFileId])
      } else {
        this.loading = false
        this.failed = true
      }
    },
  },
  render(h) {
    const containerStyle = {
      width: "100%",
      height: "100%",
      overflow: "hidden",
      background: "rgba(255,255,255,0.05)",
    }

    if (this.loading) {
      return h(
        "div",
        {
          style: {
            ...containerStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          attrs: { "aria-label": "Produktbild wird geladen" },
        },
        [h("v-progress-circular", { props: { indeterminate: true, size: 18, width: 2, color: "grey lighten-1" } })],
      )
    }

    if (this.url) {
      return h("div", { style: containerStyle }, [
        h("img", {
          attrs: {
            src: this.url,
            alt: (this.product && this.product.title) || "Produktbild",
          },
          style: {
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
          },
          on: { error: this.handleImageError },
        }),
      ])
    }

    return h(
      "div",
      {
        style: {
          ...containerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        attrs: {
          role: "img",
          "aria-label": this.failed ? "Produktbild konnte nicht geladen werden" : "Kein Produktbild verfügbar",
        },
      },
      [h("v-icon", { props: { small: true, color: "grey darken-1" } }, ["mdi-image-off"])],
    )
  },
}

const CART_STORAGE_KEY = "marketingShop_cart_v1"

export default {
  name: "MarketingShop",
  components: {
    ProductCard,
    CartDrawer,
    ProductDetailDialog,
    MyOrdersList,
    AdminOrdersList,
    OrderDetailDialog,
    ProductAdmin,
    InventoryProductThumb,
  },
  data() {
    return {
      userService,
      activeTab: "shop",
      workspaceMode: "store",

      products: [],
      loadingProducts: false,
      productSearch: "",
      productTypeFilter: "all",
      shopStudioFilter: this.getSelectedStudioId || null,
      productSort: "recommended",
      productSortOptions: [
        { text: "Empfohlen", value: "recommended" },
        { text: "Name A–Z", value: "name" },
        { text: "Neu hinzugefügt", value: "newest" },
      ],

      adminProducts: [],
      loadingAdminProducts: false,

      myOrders: [],
      loadingMyOrders: false,

      allOrders: [],
      loadingAllOrders: false,
      filterStudio: "all",
      filterStatus: "all",

      inventoryStudio: this.getSelectedStudioId || null,
      inventorySearch: "",
      loadingInventory: false,
      inventory: [],
      inventoryHeaders: [
        { text: "Bild", value: "image", sortable: false, width: "60px" },
        { text: "Produkt", value: "title" },
        { text: "Variante", value: "variant", sortable: false },
        { text: "Gesamt bestellt", value: "quantity", align: "center", width: "160px" },
      ],

      cart: [],
      cartOpen: false,
      submittingOrder: false,
      cartBump: false,
      cartBumpTimer: null,

      currentProduct: null,
      productDetailOpen: false,

      currentOrder: null,
      orderDetailOpen: false,
      loadingOrderDetail: false,

      orderSuccess: null,
      orderSuccessOpen: false,

      loadErrors: {
        products: "",
        adminProducts: "",
        myOrders: "",
        allOrders: "",
        inventory: "",
      },
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    ...mapGetters("studioStore", ["getSelectedStudioId"]),
    canManage() {
      return userService.hasRight("d_manageMarketingShop")
    },
    canFetch() {
      return userService.hasRight("d_fetchMarketingShop") || userService.hasRight("d_manageMarketingShop")
    },
    cartItemCount() {
      return this.cart.reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0)
    },
    selectedStudioName() {
      const studio = (this.storeStudios || []).find((s) => String(s._id) === String(this.getSelectedStudioId))
      return studio ? studio.name : "Kein Studio ausgewählt"
    },
    studioOptions() {
      return (this.storeStudios || []).filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    studioFilterOptions() {
      return [{ _id: "all", name: "Alle Studios" }, ...this.studioOptions]
    },
    hasProductFilters() {
      return Boolean((this.productSearch && this.productSearch.trim()) || this.productTypeFilter !== "all" || this.productSort !== "recommended")
    },
    filteredProducts() {
      let list = [...(this.products || [])]

      if (this.canManage && this.shopStudioFilter) {
        list = list.filter((product) => this.productAvailableInStudio(product, this.shopStudioFilter))
      }
      if (this.productTypeFilter && this.productTypeFilter !== "all") {
        list = list.filter((p) => (p.productType || "physical") === this.productTypeFilter)
      }
      if (this.productSearch && this.productSearch.trim()) {
        const q = this.productSearch.trim().toLowerCase()
        list = list.filter((p) => (p.title || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q))
      }

      if (this.productSort === "name") {
        list.sort((a, b) => (a.title || "").localeCompare(b.title || "", "de"))
      } else if (this.productSort === "newest") {
        list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      } else {
        list.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || (a.title || "").localeCompare(b.title || "", "de"))
      }
      return list
    },
    filteredInventory() {
      let list = this.inventory || []
      if (this.inventorySearch && this.inventorySearch.trim()) {
        const q = this.inventorySearch.trim().toLowerCase()
        list = list.filter(
          (i) =>
            (i.product.title || "").toLowerCase().includes(q) || (i.selectedOptions || []).some((o) => (o.value || "").toLowerCase().includes(q)),
        )
      }
      return list
    },
    inventoryTotalQuantity() {
      return this.filteredInventory.reduce((sum, item) => sum + (parseInt(item.totalQuantity) || 0), 0)
    },
  },
  watch: {
    activeTab(val) {
      this.workspaceMode = ["allOrders", "productAdmin"].includes(val) ? "manage" : "store"
      if (val === "myOrders") this.loadMyOrders()
      else if (val === "allOrders") this.loadAllOrders()
      else if (val === "productAdmin") this.loadAdminProducts()
      else if (val === "inventory") this.loadInventory()
    },
    workspaceMode(val) {
      const storeTabs = ["shop", "myOrders", "inventory"]
      const managementTabs = ["allOrders", "productAdmin"]
      if (val === "manage" && !managementTabs.includes(this.activeTab)) this.activeTab = "allOrders"
      if (val === "store" && !storeTabs.includes(this.activeTab)) this.activeTab = "shop"
    },
    studioOptions: {
      immediate: true,
      handler(options) {
        if (!options || !options.length) return
        const fallback = this.getSelectedStudioId || options[0]._id
        if (!this.inventoryStudio) this.inventoryStudio = fallback
        if (this.canManage && !options.some((studio) => String(studio._id) === String(this.shopStudioFilter))) {
          this.shopStudioFilter = fallback
        }
      },
    },
    inventoryStudio() {
      if (this.activeTab === "inventory") this.loadInventory()
    },
    cart: {
      deep: true,
      handler() {
        this.persistCart()
      },
    },
  },
  methods: {
    productAvailableInStudio(product, studioId) {
      const studios = product.availableInStudios || []
      if (!studios.length) return true
      return studios.some((studio) => String(studio && studio._id ? studio._id : studio) === String(studioId))
    },
    resetProductFilters() {
      this.productSearch = ""
      this.productTypeFilter = "all"
      this.productSort = "recommended"
    },
    formatInventoryOptions(options) {
      if (!options || !options.length) return "Standardausführung"
      return options.map((option) => `${option.name}: ${option.value}`).join(" · ")
    },
    getInventoryKey(item) {
      const options = (item.selectedOptions || [])
        .map((option) => `${option.name}:${option.value}`)
        .sort()
        .join("|")
      return `${item.product && item.product._id ? item.product._id : item.product}-${options}`
    },
    getLoadError(error, fallback) {
      return (error && error.response && error.response.data && error.response.data.error) || fallback
    },

    // ===== Cart =====
    loadCartFromStorage() {
      try {
        const raw = localStorage.getItem(CART_STORAGE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) this.cart = parsed
      } catch (e) {
        console.error("Cart-Storage konnte nicht gelesen werden:", e)
      }
    },
    persistCart() {
      try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(this.cart))
      } catch (e) {
        console.error("Cart-Persist fehlgeschlagen:", e)
      }
    },
    getCartItemKey(item) {
      if (!item.selectedOptions || Object.keys(item.selectedOptions).length === 0) return item.product
      const optStr = Object.entries(item.selectedOptions)
        .sort()
        .map(([k, v]) => `${k}:${v}`)
        .join(",")
      return `${item.product}_${optStr}`
    },
    isSameOptions(opt1, opt2) {
      const o1 = opt1 || {}
      const o2 = opt2 || {}
      const k1 = Object.keys(o1)
      const k2 = Object.keys(o2)
      if (k1.length !== k2.length) return false
      return k1.every((key) => o1[key] === o2[key])
    },
    getCartQuantity(productId) {
      return this.cart.filter((i) => i.product === productId).reduce((sum, i) => sum + (i.quantity || 0), 0)
    },
    getProductStockForSelection(product, selectedOptions) {
      if (!product || product.productType === "digital") return null
      if (!product.options || product.options.length === 0) {
        return product.stock !== undefined && product.stock !== null ? Number(product.stock) : null
      }

      const options = selectedOptions || {}
      const choiceIds = []
      for (const option of product.options) {
        const selectedValue = options[option.name]
        const choice = (option.choices || []).find((entry) => entry.value === selectedValue)
        if (!choice || !choice.id) return null
        choiceIds.push(choice.id)
      }
      const signature = choiceIds.sort().join(",")
      if (!product.variantStock) return null
      const stock = product.variantStock[signature]
      return stock !== undefined && stock !== null ? Number(stock) : null
    },
    bumpCart() {
      this.cartBump = false
      if (this.cartBumpTimer) clearTimeout(this.cartBumpTimer)
      this.$nextTick(() => {
        this.cartBump = true
        this.cartBumpTimer = setTimeout(() => {
          this.cartBump = false
        }, 420)
      })
    },
    addToCart({ product, quantity, selectedOptions }) {
      const isDigital = product.productType === "digital"
      const opts = selectedOptions || {}
      const existing = this.cart.find((i) => i.product === product._id && this.isSameOptions(i.selectedOptions, opts))

      if (isDigital) {
        if (existing) {
          existing.quantity = 1
        } else {
          this.cart.push({
            product: product._id,
            title: product.title,
            quantity: 1,
            minQuantity: 1,
            maxQuantity: 1,
            productType: "digital",
            selectedOptions: opts,
          })
        }
      } else {
        const requestedQuantity = Math.max(parseInt(quantity) || product.minQuantity || 1, 1)
        const newQty = (existing ? existing.quantity : 0) + requestedQuantity
        const stock = this.getProductStockForSelection(product, opts)
        const configuredMax = product.maxQuantity || 100
        const max = stock === null ? configuredMax : Math.min(configuredMax, stock)
        const min = product.minQuantity || 1

        if (max < min) {
          const message = stock <= 0 ? "Dieses Produkt ist aktuell ausverkauft." : `Die Mindestmenge von ${min} Stück ist aktuell nicht verfügbar.`
          if (this.$toast) this.$toast.error(message)
          return
        }

        const final = Math.min(newQty, max)

        if (existing) {
          existing.quantity = final
        } else {
          this.cart.push({
            product: product._id,
            title: product.title,
            quantity: Math.max(final, min),
            minQuantity: min,
            maxQuantity: configuredMax,
            productType: "physical",
            selectedOptions: opts,
          })
        }

        if (final < newQty && this.$toast) {
          this.$toast.info(`Maximal ${max} Stück verfügbar – die Menge wurde angepasst.`)
        }
      }

      this.bumpCart()
      this.$toast && this.$toast.success(`${product.title} wurde zum Warenkorb hinzugefügt`)
    },
    updateCartQuantity({ itemKey, quantity }) {
      const item = this.cart.find((i) => this.getCartItemKey(i) === itemKey)
      if (!item) return
      if (item.productType === "digital") {
        item.quantity = 1
        return
      }
      const min = item.minQuantity || 1
      const max = item.maxQuantity || 100
      item.quantity = Math.max(min, Math.min(max, quantity))
    },
    removeFromCart(itemKey) {
      this.cart = this.cart.filter((i) => this.getCartItemKey(i) !== itemKey)
    },
    clearCart() {
      this.cart = []
    },
    openCart() {
      this.cartOpen = true
    },

    // ===== Order Submit =====
    async submitOrder({ studio, note }) {
      if (this.cart.length === 0) return

      this.submittingOrder = true
      try {
        const payload = {
          studio,
          note: note || "",
          items: this.cart.map((i) => ({
            product: i.product,
            quantity: i.quantity,
            selectedOptions: i.selectedOptions ? Object.entries(i.selectedOptions).map(([name, value]) => ({ name, value })) : [],
          })),
        }

        const order = await api.marketingShop.createOrder(payload)

        const digitalItems = (order.items || []).filter((i) => i.productType === "digital")
        const physicalItems = (order.items || []).filter((i) => i.productType !== "digital")
        this.clearCart()
        this.cartOpen = false
        this.orderSuccess = {
          reference:
            order.bestellnummer ||
            `#${String(order._id || "")
              .slice(-6)
              .toUpperCase()}`,
          digitalItems: digitalItems.length,
          physicalItems: physicalItems.length,
        }
        this.orderSuccessOpen = true
        this.activeTab = "myOrders"
      } catch (e) {
        console.error("Bestellung fehlgeschlagen:", e)
        const msg = e.response?.data?.error || "Fehler beim Absenden der Bestellung"
        if (this.$toast) this.$toast.error(msg)
      } finally {
        this.submittingOrder = false
      }
    },

    continueShopping() {
      this.orderSuccessOpen = false
      this.activeTab = "shop"
    },
    showMyOrders() {
      this.orderSuccessOpen = false
      this.activeTab = "myOrders"
    },

    async reloadAllProducts() {
      this.loadAdminProducts()
      this.loadProducts()
    },

    async loadProducts() {
      this.loadingProducts = true
      this.loadErrors.products = ""
      try {
        this.products = await api.marketingShop.fetchProducts()
      } catch (e) {
        console.error("Fehler beim Laden der Produkte:", e)
        this.loadErrors.products = this.getLoadError(e, "Die Produkte konnten nicht geladen werden.")
      } finally {
        this.loadingProducts = false
      }
    },
    async loadAdminProducts() {
      if (!this.canManage) return
      this.loadingAdminProducts = true
      this.loadErrors.adminProducts = ""
      try {
        this.adminProducts = await api.marketingShop.fetchProducts({ all: true })
      } catch (e) {
        console.error("Fehler beim Laden:", e)
        this.loadErrors.adminProducts = this.getLoadError(e, "Die Produktverwaltung konnte nicht geladen werden.")
      } finally {
        this.loadingAdminProducts = false
      }
    },
    async loadMyOrders() {
      this.loadingMyOrders = true
      this.loadErrors.myOrders = ""
      try {
        const params = this.canManage ? { mine: true } : {}
        this.myOrders = await api.marketingShop.fetchOrders(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
        this.loadErrors.myOrders = this.getLoadError(e, "Deine Bestellungen konnten nicht geladen werden.")
      } finally {
        this.loadingMyOrders = false
      }
    },
    async loadAllOrders() {
      if (!this.canManage) return
      this.loadingAllOrders = true
      this.loadErrors.allOrders = ""
      try {
        // Alle zugänglichen Bestellungen einmal laden; Suche und Filter reagieren danach ohne neue Requests.
        this.allOrders = await api.marketingShop.fetchOrders()
      } catch (e) {
        console.error("Fehler beim Laden:", e)
        this.loadErrors.allOrders = this.getLoadError(e, "Die Bestellungen konnten nicht geladen werden.")
      } finally {
        this.loadingAllOrders = false
      }
    },
    async loadInventory() {
      const studioId = this.inventoryStudio || this.getSelectedStudioId
      if (!studioId) {
        this.inventory = []
        this.loadErrors.inventory = "Bitte wähle zuerst ein Studio aus."
        return
      }
      this.loadingInventory = true
      this.loadErrors.inventory = ""
      try {
        this.inventory = await api.marketingShop.fetchInventory({ studio: studioId })
      } catch (e) {
        console.error("Fehler beim Laden des Inventars:", e)
        this.loadErrors.inventory = this.getLoadError(e, "Die bestellten Werbemittel konnten nicht geladen werden.")
      } finally {
        this.loadingInventory = false
      }
    },
    getSelectedStudioName() {
      return this.selectedStudioName
    },

    openProductDetail(product) {
      this.currentProduct = product
      this.productDetailOpen = true
    },
    async openOrderDetail(order) {
      this.loadingOrderDetail = true
      try {
        this.currentOrder = await api.marketingShop.fetchOrder(order._id)
        this.orderDetailOpen = true
      } catch (e) {
        console.error("Order konnte nicht geladen werden:", e)
        if (this.$toast) this.$toast.error(this.getLoadError(e, "Die Bestellung konnte nicht geöffnet werden."))
      } finally {
        this.loadingOrderDetail = false
      }
    },
    async reloadCurrentOrder() {
      if (!this.currentOrder) return
      try {
        this.currentOrder = await api.marketingShop.fetchOrder(this.currentOrder._id)
      } catch (e) {
        console.error(e)
      }
    },
    handleOrderUpdated() {
      if (this.activeTab === "myOrders") this.loadMyOrders()
      if (this.activeTab === "allOrders") this.loadAllOrders()
    },

    setupSocketListeners() {
      socketService.on("marketingOrder:new", this.handleNewOrder)
      socketService.on("marketingOrder:updated", this.handleOrderStatusUpdate)
      socketService.on("marketingOrder:commented", this.handleCommentUpdate)
    },

    teardownSocketListeners() {
      socketService.off("marketingOrder:new", this.handleNewOrder)
      socketService.off("marketingOrder:updated", this.handleOrderStatusUpdate)
      socketService.off("marketingOrder:commented", this.handleCommentUpdate)
    },

    handleNewOrder() {
      if (this.activeTab === "allOrders") this.loadAllOrders()
      this.$toast?.info("Eine neue Bestellung ist eingegangen!")
    },

    handleOrderStatusUpdate(payload) {
      if (this.activeTab === "myOrders") this.loadMyOrders()
      if (this.activeTab === "allOrders") this.loadAllOrders()
      if (this.currentOrder && this.currentOrder._id === payload.orderId) {
        this.reloadCurrentOrder()
      }
    },

    handleCommentUpdate(payload) {
      if (this.currentOrder && this.currentOrder._id === payload.orderId) {
        this.reloadCurrentOrder()
      }
    },
  },
  created() {
    this.loadCartFromStorage()
    this.loadProducts()
  },
  mounted() {
    this.setupSocketListeners()
  },
  beforeDestroy() {
    this.teardownSocketListeners()
    if (this.cartBumpTimer) clearTimeout(this.cartBumpTimer)
  },
}
</script>

<style scoped>
.marketing-shop {
  --shop-primary: #00a84f;
  --shop-primary-soft: rgba(0, 168, 79, 0.14);
  --shop-surface: rgba(255, 255, 255, 0.045);
  --shop-surface-strong: rgba(255, 255, 255, 0.075);
  --shop-border: rgba(255, 255, 255, 0.09);
  --shop-text-muted: rgba(255, 255, 255, 0.64);
  --shop-radius: 16px;
  min-height: 100vh;
  color: #fff;
}

.content-shell {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding-right: 24px;
  padding-left: 24px;
}

.shop-header {
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid var(--shop-border);
  background: radial-gradient(circle at 82% 0%, rgba(0, 168, 79, 0.15), transparent 34%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.025), rgba(255, 255, 255, 0));
}

.hero-content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px;
  padding-top: 34px;
  padding-bottom: 24px;
}

.hero-copy {
  max-width: 760px;
}

.eyebrow {
  margin-bottom: 6px;
  color: #73dd9d;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1 {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 750;
  letter-spacing: -0.035em;
  line-height: 1.08;
}

.hero-copy > p {
  max-width: 680px;
  margin: 10px 0 16px;
  color: var(--shop-text-muted);
  font-size: 1rem;
  line-height: 1.55;
}

.context-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  color: rgba(255, 255, 255, 0.48);
  font-size: 0.78rem;
}

.context-chip {
  background: rgba(0, 168, 79, 0.07) !important;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.workspace-switch {
  overflow: hidden;
  border: 1px solid var(--shop-border);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.14);
}

.workspace-switch ::v-deep .v-btn {
  min-height: 42px;
  padding: 0 16px;
}

.tab-shell {
  display: flex;
  align-items: center;
  gap: 20px;
}

.tab-context {
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.shop-tabs {
  min-width: 0;
}

.cart-btn {
  border: 1px solid var(--shop-border);
  background: rgba(255, 255, 255, 0.045);
  transition: transform 180ms ease, background-color 180ms ease;
}

.cart-btn ::v-deep .v-icon {
  color: #fff !important;
  opacity: 1;
}

.cart-btn:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.09);
}

.cart-bump {
  animation: cart-bump 420ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

@keyframes cart-bump {
  0%,
  100% {
    transform: scale(1);
  }
  42% {
    transform: scale(1.16) rotate(-5deg);
  }
  68% {
    transform: scale(0.96) rotate(2deg);
  }
}

.shop-content {
  padding-bottom: 96px;
}

.content-section {
  padding-top: 30px;
  padding-bottom: 30px;
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
}

.section-heading h2 {
  margin: 0;
  font-size: clamp(1.35rem, 2vw, 1.75rem);
  letter-spacing: -0.02em;
}

.section-heading p {
  max-width: 760px;
  margin: 5px 0 0;
  color: var(--shop-text-muted);
  line-height: 1.5;
}

.result-count {
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.82rem;
  font-weight: 600;
}

.filter-panel {
  margin-bottom: 24px;
  padding: 18px;
  border: 1px solid var(--shop-border);
  border-radius: var(--shop-radius) !important;
  background: var(--shop-surface) !important;
  backdrop-filter: blur(14px);
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.065);
}

.type-toggle {
  flex: 0 0 auto;
  border-radius: 10px;
}

.type-toggle ::v-deep .v-btn {
  min-height: 40px;
}

.reset-filter {
  min-height: 40px;
  margin-left: auto;
}

.state-alert {
  margin-bottom: 20px;
  border-radius: 14px;
}

.state-alert__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 22px;
}

.product-skeleton {
  overflow: hidden;
  min-height: 390px;
  border: 1px solid var(--shop-border);
  border-radius: var(--shop-radius);
  background: var(--shop-surface) !important;
}

.product-list-enter-active,
.product-list-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.product-list-move {
  transition: transform 200ms ease;
}

.product-list-enter,
.product-list-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.empty-state {
  display: flex;
  min-height: 320px;
  padding: 40px 20px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px dashed rgba(255, 255, 255, 0.13);
  border-radius: var(--shop-radius);
  background: rgba(255, 255, 255, 0.018);
  text-align: center;
}

.empty-state__icon {
  display: grid;
  width: 76px;
  height: 76px;
  margin-bottom: 18px;
  place-items: center;
  border-radius: 24px;
  background: var(--shop-primary-soft);
}

.empty-state h3 {
  margin: 0 0 6px;
  font-size: 1.18rem;
}

.empty-state p {
  max-width: 480px;
  margin: 0 0 20px;
  color: var(--shop-text-muted);
}

.inventory-filter {
  margin-bottom: 18px;
}

.studio-readonly {
  display: flex;
  min-height: 40px;
  padding: 4px 12px;
  justify-content: center;
  flex-direction: column;
  border-left: 2px solid var(--shop-primary);
}

.studio-readonly span {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.68rem;
  text-transform: uppercase;
}

.data-surface {
  overflow: hidden;
  border: 1px solid var(--shop-border);
  border-radius: var(--shop-radius) !important;
  background: var(--shop-surface) !important;
}

.quantity-chip {
  min-width: 72px;
  justify-content: center;
}

.inventory-skeletons {
  overflow: hidden;
  border: 1px solid var(--shop-border);
  border-radius: var(--shop-radius);
}

.inventory-mobile-list {
  display: grid;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  gap: 10px;
  overflow: hidden;
}

.inventory-mobile-card {
  display: grid;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  grid-template-areas:
    "media body"
    "media quantity";
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 8px 12px;
  padding: 12px;
  overflow: hidden;
  border: 1px solid var(--shop-border);
  border-radius: 14px !important;
  background: var(--shop-surface) !important;
}

.inventory-mobile-card__media {
  display: block;
  width: 64px;
  height: 64px;
  grid-area: media;
  align-self: start;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.055);
}

.inventory-mobile-card__body {
  display: flex;
  grid-area: body;
  min-width: 0;
  flex-direction: column;
}

.inventory-mobile-card__body strong {
  display: -webkit-box;
  min-width: 0;
  font-size: 0.9rem;
  line-height: 1.32;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.inventory-mobile-card__body span {
  display: -webkit-box;
  min-width: 0;
  margin-top: 3px;
  color: var(--shop-text-muted);
  font-size: 0.73rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.inventory-mobile-card__quantity {
  display: flex;
  grid-area: quantity;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.inventory-mobile-card__quantity > span {
  min-width: 0;
  color: rgba(255, 255, 255, 0.48);
  font-size: 0.68rem;
  line-height: 1.2;
}

.inventory-mobile-card__quantity .quantity-chip {
  flex: 0 0 auto;
}

.inventory-mobile-filter-controls {
  display: grid;
  min-width: 0;
  gap: 10px;
}

.inventory-mobile-filter-row {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
}

.inventory-mobile-search {
  min-width: 0;
  flex: 1 1 auto;
}

.inventory-mobile-refresh {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  margin: 0;
  border-color: rgba(255, 255, 255, 0.16) !important;
}

.inventory-mobile-studio {
  min-height: 44px;
  padding-top: 3px;
  padding-bottom: 3px;
}

.order-success-card {
  padding: 36px;
  border: 1px solid rgba(0, 168, 79, 0.28);
  border-radius: 20px !important;
  background: radial-gradient(circle at 50% 0%, rgba(0, 168, 79, 0.18), transparent 42%), #202321 !important;
  text-align: center;
}

.success-mark {
  display: grid;
  width: 76px;
  height: 76px;
  margin: 0 auto 20px;
  place-items: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b85b, #007e38);
  box-shadow: 0 14px 34px rgba(0, 168, 79, 0.26);
  animation: success-in 340ms cubic-bezier(0.2, 0.9, 0.3, 1.25) both;
}

@keyframes success-in {
  from {
    opacity: 0;
    transform: scale(0.65);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.order-success-card h2 {
  margin: 0 0 8px;
}

.order-success-card > p {
  margin: 0 0 20px;
  color: var(--shop-text-muted);
}

.success-info {
  text-align: left;
}

.success-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

.loading-detail-card {
  border: 1px solid var(--shop-border);
  border-radius: 16px !important;
}

.transparent {
  background: transparent !important;
}

.marketing-shop ::v-deep .v-btn:focus-visible,
.marketing-shop ::v-deep .v-tab:focus-visible,
.marketing-shop ::v-deep input:focus-visible {
  outline: 3px solid rgba(91, 207, 136, 0.72);
  outline-offset: 2px;
}

@media (max-width: 960px) {
  .hero-content {
    align-items: stretch;
    flex-direction: column;
  }

  .hero-actions {
    justify-content: space-between;
  }

  .section-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
}

@media (max-width: 700px) {
  .content-shell {
    padding-right: 14px;
    padding-left: 14px;
  }

  .hero-content {
    gap: 20px;
    padding-top: 24px;
    padding-bottom: 18px;
  }

  .hero-copy h1 {
    font-size: 2rem;
  }

  .hero-copy > p {
    font-size: 0.9rem;
  }

  .context-line > span {
    display: none;
  }

  .workspace-switch {
    flex: 1;
  }

  .workspace-switch ::v-deep .v-btn {
    flex: 1;
    padding: 0 10px;
  }

  .tab-shell {
    display: block;
    padding-right: 0;
    padding-left: 0;
  }

  .tab-context {
    display: none;
  }

  .shop-tabs ::v-deep .v-tab {
    min-width: auto;
    padding: 0 14px;
    font-size: 0.77rem;
  }

  .content-section {
    padding-top: 22px;
  }

  .filter-panel {
    padding: 14px;
  }

  .filter-row {
    align-items: stretch;
    flex-direction: column;
  }

  .type-toggle {
    display: flex;
    width: 100%;
  }

  .type-toggle ::v-deep .v-btn {
    min-width: 0;
    flex: 1;
    padding: 0 8px;
  }

  .product-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .state-alert__content,
  .success-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .order-success-card {
    padding: 28px 20px 20px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cart-btn,
  .product-list-enter-active,
  .product-list-leave-active,
  .product-list-move,
  .success-mark {
    animation: none !important;
    transition: none !important;
  }
}
</style>
